import React, { useState } from "react";
import { Share2, Copy, MessageCircle, Twitter, Mail, Check } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

// Compose a personal-tone share message from a property. Q5(a).
function composeMessage(property) {
  const rentTxt = property.category === "short_stay"
    ? `₹${(property.category_fields?.price_per_night || 0).toLocaleString("en-IN")}/night`
    : `₹${(property.rent || 0).toLocaleString("en-IN")}/mo`;
  return `Check out this property I found on LocaNest: ${property.title}, ${property.city} — ${rentTxt}.`;
}

// Reusable Share button.
//  - variant="icon" : small round icon (used on PropertyCard, prevents Link click via stopPropagation)
//  - variant="full" : full-width labelled Button (used on PropertyDetail sidebar)
export default function ShareButton({ property, variant = "icon", testId = "share-btn" }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const url = `${window.location.origin}/property/${property.id}`;
  const message = composeMessage(property);
  const composed = `${message} ${url}`;

  const doNativeShare = async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (navigator.share) {
      try {
        await navigator.share({ title: property.title, text: message, url });
        return true;
      } catch (err) {
        // User dismissed — fall through to popover
        if (err?.name !== "AbortError") toast.error("Share failed");
      }
    }
    return false;
  };

  const handleClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Prefer native share on mobile; open popover on desktop
    const shared = await doNativeShare(e);
    if (!shared) setOpen((v) => !v);
  };

  const copy = async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    try {
      await navigator.clipboard.writeText(composed);
      setCopied(true);
      toast.success("Link copied — paste it anywhere");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback textarea copy
      const ta = document.createElement("textarea");
      ta.value = composed;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); toast.success("Link copied"); setCopied(true); setTimeout(() => setCopied(false), 2000); }
      catch { toast.error("Could not copy — please long-press the link"); }
      document.body.removeChild(ta);
    }
  };

  const trigger = variant === "icon" ? (
    <button
      onClick={handleClick}
      aria-label="Share property"
      data-testid={testId}
      className="absolute top-3 right-14 p-2 rounded-full backdrop-blur bg-[#FDFBF7]/90 text-[#1A1F2C] hover:bg-[#FDFBF7]"
    >
      <Share2 size={16} />
    </button>
  ) : (
    <Button
      variant="outline"
      onClick={handleClick}
      className="w-full rounded-md"
      data-testid={testId}
    >
      <Share2 size={14} className="mr-1.5" /> Share this property
    </Button>
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent className="w-64 p-2 bg-[#FDFBF7] border border-[#1A1F2C]/15 shadow-lg z-50" align="end" onClick={(e) => e.stopPropagation()}>
        <div className="text-xs uppercase tracking-[0.2em] text-[#C85A32] px-2 pt-1 pb-2">Share via</div>
        <a
          href={`https://wa.me/?text=${encodeURIComponent(composed)}`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="flex items-center gap-3 px-2 py-2 rounded hover:bg-[#F0EBE1] text-sm"
          data-testid={`${testId}-whatsapp`}
        >
          <MessageCircle size={16} className="text-[#25D366]" /> WhatsApp
        </a>
        <a
          href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(composed)}`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="flex items-center gap-3 px-2 py-2 rounded hover:bg-[#F0EBE1] text-sm"
          data-testid={`${testId}-twitter`}
        >
          <Twitter size={16} className="text-[#1DA1F2]" /> Twitter / X
        </a>
        <a
          href={`mailto:?subject=${encodeURIComponent(property.title)}&body=${encodeURIComponent(composed)}`}
          onClick={(e) => e.stopPropagation()}
          className="flex items-center gap-3 px-2 py-2 rounded hover:bg-[#F0EBE1] text-sm"
          data-testid={`${testId}-email`}
        >
          <Mail size={16} className="text-[#5C6B7A]" /> Email
        </a>
        <button
          onClick={copy}
          className="w-full flex items-center gap-3 px-2 py-2 rounded hover:bg-[#F0EBE1] text-sm text-left"
          data-testid={`${testId}-copy`}
        >
          {copied ? <Check size={16} className="text-green-600" /> : <Copy size={16} className="text-[#5C6B7A]" />}
          {copied ? "Copied!" : "Copy link"}
        </button>
      </PopoverContent>
    </Popover>
  );
}
